import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Send, CheckCircle2, Clock, ArrowRight, Mail } from "lucide-react";
import { getUserId } from "../lib/api";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (n) => `${(Number(n) || 0).toFixed(2)} €`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

const STORAGE_EMAIL = "papicoo_sender_email";

export default function MyTransfers() {
  const [email, setEmail] = useState(() => localStorage.getItem(STORAGE_EMAIL) || "");
  const [emailDraft, setEmailDraft] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (e = email) => {
    const uid = getUserId();
    if (!uid && !e) return;
    setLoading(true);
    try {
      const params = {};
      if (uid) params.user_id = uid;
      if (e) params.email = e;
      const { data } = await axios.get(`${API}/me/transfers`, { params });
      setRows(data);
    } catch { toast.error(T("Impossible de charger l'historique.")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveEmail = () => {
    if (!emailDraft.trim()) { toast.error(T("Indique ton email d'expéditeur.")); return; }
    localStorage.setItem(STORAGE_EMAIL, emailDraft.trim());
    setEmail(emailDraft.trim());
    setEmailDraft("");
    load(emailDraft.trim());
    toast.success(T("Email enregistré — Papicoo te retrouve."));
  };

  const totalSent = rows.filter((r) => r.payment_status === "paid").reduce((s, r) => s + Number(r.send_eur || 0), 0);
  const totalFees = rows.filter((r) => r.payment_status === "paid").reduce((s, r) => s + Number(r.fee_eur || 0), 0);

  return (
    <div data-testid="page-my-transfers" className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Mon espace")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Mes transferts.")}
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-2xl leading-relaxed">
        {T("Retrouve tous tes envois vers le Sénégal. Papicoo garde une trace pour toi — montants, destinataires, statut du payout.")}
      </p>

      {!email && (
        <div data-testid="email-prompt" className="mt-6 papicoo-card p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">
            <Mail size={14} className="text-[color:var(--papicoo-gold)]" />
            {T("Identifie-toi pour retrouver tes transferts")}
          </div>
          <p className="text-sm text-[color:var(--papicoo-muted)] mt-1 italic">
            {T("Saisis ton email d'expéditeur (celui utilisé pour payer) — Papicoo retrouvera l'historique sans mot de passe.")}
          </p>
          <div className="mt-3 flex gap-2 flex-wrap">
            <input
              data-testid="email-input"
              type="email"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              placeholder={T("ton.email@exemple.com")}
              className="flex-1 min-w-[200px] bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-full px-4 py-2 text-sm outline-none"
            />
            <button data-testid="email-save" onClick={saveEmail} className="px-5 py-2 rounded-full papicoo-btn-primary text-sm inline-flex items-center gap-1.5">
              {T("Retrouver mes transferts")} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {(rows.length > 0 || email) && (
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <div className="papicoo-card p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Total envoyé au pays")}</div>
            <div className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-1">{fmtEur(totalSent)}</div>
            <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">Dont frais Papicoo : {fmtEur(totalFees)}</div>
          </div>
          <div className="papicoo-card p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Transferts effectués")}</div>
            <div className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-1">{rows.filter((r) => r.payment_status === "paid").length}</div>
            <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">sur {rows.length} créés</div>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading && <div className="text-center italic text-[color:var(--papicoo-muted)] py-6">{T("Chargement…")}</div>}
        {!loading && rows.length === 0 && email && (
          <div className="papicoo-card p-6 text-center text-sm text-[color:var(--papicoo-muted)] italic">
            {T("Aucun transfert pour l'instant.")} <Link to="/transfer" className="papicoo-link-underline not-italic">{T("Faire un premier envoi")}</Link>.
          </div>
        )}
        {rows.map((r) => {
          const paid = r.payment_status === "paid";
          return (
            <div key={r.session_id || r.created_at} data-testid={`transfer-row-${r.session_id || r.created_at}`} className="papicoo-card p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Send size={14} className="text-[color:var(--papicoo-gold)] shrink-0" />
                    <div className="font-display text-xl text-[color:var(--papicoo-ink)]">{fmtEur(r.send_eur)} → {r.recipient_name}</div>
                  </div>
                  <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">
                    {r.payout_method} · {r.recipient_phone} · {fmtDate(r.created_at)}
                  </div>
                  {r.note && <div className="text-sm italic text-[color:var(--papicoo-muted)] mt-2">« {T(r.note)} »</div>}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {paid ? <CheckCircle2 size={11} /> : <Clock size={11} />}
                    {paid ? T("Payé") : (r.payment_status || T("en attente"))}
                  </span>
                  {paid && (
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${r.payout_done ? "bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)]" : "bg-white border border-[color:var(--papicoo-border)] text-[color:var(--papicoo-muted)]"}`}>
                      {r.payout_done ? `✓ Reçu ${r.payout_marked_at ? `(${fmtDate(r.payout_marked_at)})` : ""}` : T("Payout en cours")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {email && (
        <div className="mt-8 text-xs text-[color:var(--papicoo-muted)] text-center">
          {T("Connecté avec")} <strong>{email}</strong> ·{" "}
          <button onClick={() => { localStorage.removeItem(STORAGE_EMAIL); setEmail(""); setRows([]); }} className="papicoo-link-underline">
            {T("changer d'email")}
          </button>
        </div>
      )}
    </div>
  );
}
